// Praat met de backend (contract §5): JSON in en uit, sessiecookie, foutvorm { error, code, details }.

export interface ErrorDetail {
  field: string;
  message: string;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
    readonly details: ErrorDetail[] = []
  ) {
    super(message);
  }
}

/** Fouten die de gebruiker zelf kan oplossen: getoond als "Controle niet geslaagd". */
export const CHECK_ERROR_CODES = ["rule", "duplicate", "conflict", "validation"];

/** Wordt opgeroepen bij 401 (sessie weg) en 403 2fa_setup_required; de app stelt dit in. */
export const apiEvents = {
  onUnauthenticated: () => {},
  onTwoFactorRequired: () => {}
};

export async function api<T>(path: string, options: { method?: string; body?: unknown; form?: FormData; quiet401?: boolean } = {}): Promise<T> {
  const init: RequestInit = { method: options.method ?? (options.body !== undefined || options.form ? "POST" : "GET"), credentials: "same-origin", cache: "no-store" };
  if (options.form) init.body = options.form;
  else if (options.body !== undefined) {
    init.body = JSON.stringify(options.body);
    init.headers = { "Content-Type": "application/json" };
  }
  let response: Response;
  try {
    response = await fetch(`/api/${path.replace(/^\//, "")}`, init);
  } catch {
    throw new ApiError("De server is niet bereikbaar. Controleer je verbinding.", 0, "network");
  }
  if (response.status === 204) return undefined as T;
  const data = (await response.json().catch(() => null)) as { error?: string; code?: string; details?: ErrorDetail[] } | null;
  if (!response.ok) {
    const error = new ApiError(data?.error ?? `Serverfout ${response.status}`, response.status, data?.code ?? "", data?.details ?? []);
    if (response.status === 401 && !options.quiet401) apiEvents.onUnauthenticated();
    if (response.status === 403 && error.code === "2fa_setup_required") apiEvents.onTwoFactorRequired();
    throw error;
  }
  return data as T;
}

/** Titel boven een foutmelding in een dialoog. */
export function errorTitle(error: unknown) {
  if (error instanceof ApiError && CHECK_ERROR_CODES.includes(error.code)) return "Controle niet geslaagd";
  if (error instanceof ApiError && error.status === 403) return "Geen toegang";
  return "Actie mislukt";
}
